import { TableCell } from "@mui/material";
import { ScheduledGame } from "src/types/TournamentTypes";
import { ApprovedTeam } from "src/types/TournamentTypes";
import { useDroppable } from "@dnd-kit/core";
import {
  getColorForTournament,
  getColorForLeagueChip,
} from "./TournamentConstants";
import GameCard from "src/components/TournamentScheduler/GameCard";
import { GameLayout } from "src/components/TournamentScheduler/CalendarTable";

function CalendarCell({
    date,
    time,
    games,
    teamName,
    leagueNameMap,
    tournamentNameMap,
    allScheduledGames,
    teamsMap,
    getPlayerIdsOnTeam,
    hasSchedule,
    rowBackground,
    onGameClick,
    gameLayoutMap,
  }: {
    date: string;
    time: string;
    games: ScheduledGame[];
    teamName: (id: number) => string;
    leagueNameMap: Record<number, { name: string; groupId: number }>;
    tournamentNameMap: Record<number, string>;
    allScheduledGames: ScheduledGame[];
    teamsMap: Record<number, ApprovedTeam>;
    getPlayerIdsOnTeam: (id: number) => number[];
    hasSchedule: boolean;
    rowBackground: string;
    onGameClick: (game: ScheduledGame) => void;
    gameLayoutMap: Map<number, GameLayout>;
  }) {
    const { setNodeRef, isOver } = useDroppable({
      id: `slot-${date}__${time}`,
    });
  
    const bg = isOver && hasSchedule
      ? "rgba(25, 118, 210, 0.12)"
      : games.length > 0
      ? "rgba(0, 0, 0, 0.02)"
      : rowBackground; // Use the alternating row background
  
    // Calculate height per minute (each 30-min slot is 28px, so 30 minutes = 28px, 60 minutes = 56px)
    const PX_PER_MINUTE = 28 / 30; // 0.933px per minute
  
    return (
      <TableCell
        ref={setNodeRef}
        sx={{
          minHeight: 28,
          height: 28,
          verticalAlign: "top",
          backgroundColor: bg,
          transition: "background-color 0.15s ease-in-out",
          p: 0,
          position: "relative",
          overflow: "visible",
          borderRight: "1px solid rgba(0, 0, 0, 0.12)",
        }}
      >
        {games.map((game, idx) => {
          const leagueInfo = leagueNameMap[game.tournament_id];
          // Tournament-based card background colour
          const color = getColorForTournament(game.tournament_group_id);
          // League-based chip colour
          const leagueChipColor = getColorForLeagueChip(game.tournament_id);

          // Calculate height based on duration (28px per 30 min = 0.933px per minute)
          const gameHeight = Math.max(20, game.duration_minutes * PX_PER_MINUTE);
          // Calculate how many 30-min slots this game spans
          const rowSpan = Math.ceil(game.duration_minutes / 30);
  
          // Use pre-computed overlap-aware layout
          const layout = gameLayoutMap.get(game.scheduled_event_id);
          const totalColumns = layout?.totalColumns ?? 1;
          const columnIndex = layout?.columnIndex ?? idx;
          const widthPercent = 100 / totalColumns;
          const leftPercent = columnIndex * widthPercent;
          
          return (
            <GameCard
              key={game.scheduled_event_id}
              game={game}
              teamName={teamName}
              leagueName={leagueInfo?.name ?? `League ${game.tournament_id}`}
              tournamentName={tournamentNameMap[game.tournament_group_id] ?? "Unknown"}
              color={color}
              leagueChipColor={leagueChipColor}
              allScheduledGames={allScheduledGames}
              teamsMap={teamsMap}
              getPlayerIdsOnTeam={getPlayerIdsOnTeam}
              onGameClick={onGameClick}
              height={gameHeight}
              rowSpan={rowSpan}
              leftPercent={leftPercent}
              widthPercent={widthPercent}
            />
          );
        })}
      </TableCell>
    );
  }

export default CalendarCell;